import { HttpErrorResponse } from '@angular/common/http';

/**
 * Stable machine-readable codes the backend returns in errors[0].error_code.
 * Matched instead of the message, which is localized and may change.
 */
export const API_ERROR_CODES = {
  PARTNER_BLOCKED: 'partner_blocked',
  PAST_PAID_CANCELLATION_BLOCKED: 'past_paid_cancellation_blocked',
  ADMIN_APPOINTMENT_REBOOKING_DISABLED: 'admin_appointment_rebooking_disabled',
  ADMIN_EVENT_REBOOKING_DISABLED: 'admin_event_rebooking_disabled',
  ADMIN_CANCELLATION_DISABLED: 'admin_cancellation_disabled',
  CUSTOMER_CANCELLATION_DISABLED: 'customer_cancellation_disabled',
  CUSTOMER_REBOOKING_LEADTIME_EXCEEDED: 'customer_rebooking_leadtime_exceeded',
  LAST_WORKER_CANNOT_BE_DELETED: 'last_worker_cannot_be_deleted',
  PAYMENT_TYPE_LOCKED_BY_ONLINE_PAYMENT: 'payment_type_locked_by_online_payment',
} as const;

/**
 * Translation key per error_code, for codes whose message is the same wherever the
 * error surfaces. Codes needing per-surface wording (e.g. the cancel-subscription
 * admin vs partner variants) keep their own maps in their domain constants.
 */
export const API_ERROR_MESSAGE_KEYS: Record<string, string> = {
  [API_ERROR_CODES.PAST_PAID_CANCELLATION_BLOCKED]: 'general.past_paid_cancellation_blocked',
  [API_ERROR_CODES.ADMIN_APPOINTMENT_REBOOKING_DISABLED]: 'general.admin_appointment_rebooking_disabled',
  [API_ERROR_CODES.ADMIN_EVENT_REBOOKING_DISABLED]: 'general.admin_event_rebooking_disabled',
  [API_ERROR_CODES.ADMIN_CANCELLATION_DISABLED]: 'general.admin_cancellation_disabled',
  [API_ERROR_CODES.CUSTOMER_CANCELLATION_DISABLED]: 'general.customer_cancellation_disabled',
  [API_ERROR_CODES.CUSTOMER_REBOOKING_LEADTIME_EXCEEDED]: 'general.customer_rebooking_leadtime_exceeded',
  [API_ERROR_CODES.LAST_WORKER_CANNOT_BE_DELETED]: 'general.last_worker_cannot_be_deleted',
  [API_ERROR_CODES.PAYMENT_TYPE_LOCKED_BY_ONLINE_PAYMENT]: 'general.payment_type_locked_by_online_payment',
};

/**
 * Resolves the toast key for a failed request: the code-specific translation when the
 * backend sent a known error_code, otherwise the caller's own generic key — so an
 * unmapped code, a validation error or a network failure shows exactly what the
 * surface showed before.
 */
export function resolveApiErrorKey(error: HttpErrorResponse, fallbackKey: string): string {
  const errorCode = error?.error?.errors?.[0]?.error_code;

  // Own-property check, not a bare lookup: a code matching an Object.prototype member
  // (constructor, toString, __proto__) would otherwise return a function, skip the ??
  // fallback, and throw inside the caller's error handler instead of showing a toast.
  return typeof errorCode === 'string' && Object.prototype.hasOwnProperty.call(API_ERROR_MESSAGE_KEYS, errorCode)
    ? API_ERROR_MESSAGE_KEYS[errorCode]
    : fallbackKey;
}
